import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import EventFeatureFilterEditor, { buildEventFeatureFilter } from '@/components/admin/events/EventFeatureFilterEditor.tsx';
import ReminderMinutesEditor from '@/components/admin/events/ReminderMinutesEditor.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import { reminderRowsToMinutes } from '@/utils/event-helpers';
import type { ReminderMinutesRow } from '@/utils/event-helpers';
import type { Event, FeatureType } from '@/models';

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated: (event: Event) => void;
}

export default function CreateEventModal({ isOpen, onClose, onEventCreated }: CreateEventModalProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [link, setLink] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [features, setFeatures] = useState<FeatureType[]>([]);
  const [excludeCommunityMembers, setExcludeCommunityMembers] = useState(false);
  const [onlyCommunityMembers, setOnlyCommunityMembers] = useState(false);
  const [reminderRows, setReminderRows] = useState<ReminderMinutesRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Set default date/time values
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const defaultDate = `${year}-${month}-${day}`;

      setStartDate(defaultDate);
      setStartTime('18:00');
      setEndDate(defaultDate);
      setEndTime('19:00');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim()) {
      showErrorToast('Bitte geben Sie einen Betreff ein');
      return;
    }

    if (!startDate || !startTime) {
      showErrorToast('Bitte geben Sie Startdatum und -zeit ein');
      return;
    }

    if (!endDate || !endTime) {
      showErrorToast('Bitte geben Sie Enddatum und -zeit ein');
      return;
    }

    if (!link.trim()) {
      showErrorToast('Bitte geben Sie einen Link ein');
      return;
    }

    if (!isPublic && features.length === 0) {
      showErrorToast('Bitte wählen Sie mindestens ein Feature aus oder markieren Sie das Event als öffentlich');
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      showErrorToast('Bitte geben Sie ein gültiges Datum und eine gültige Uhrzeit ein');
      return;
    }

    // Validate that end is after start
    if (endDateTime < startDateTime) {
      showErrorToast('Das Enddatum muss nach dem Startdatum liegen');
      return;
    }

    const reminderResult = reminderRowsToMinutes(reminderRows);
    if ('error' in reminderResult) {
      showErrorToast(reminderResult.error);
      return;
    }

    setLoading(true);

    try {
      const newEvent = await APIRoutes.createEvent({
        subject: subject.trim(),
        description: description.trim(),
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        link: link.trim(),
        featureFilter: buildEventFeatureFilter(isPublic, features, excludeCommunityMembers, onlyCommunityMembers),
        reminderMinutes: reminderResult.minutes
      });
      onEventCreated(newEvent);
      onClose();

      // Reset form
      resetForm();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Erstellen des Events');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
    setLink('');
    setIsPublic(false);
    setFeatures([]);
    setExcludeCommunityMembers(false);
    setOnlyCommunityMembers(false);
    setReminderRows([]);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Neues Event erstellen"
      icon={<FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 text-purple-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Betreff *
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="z.B. Live-Webinar: Marktanalyse"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Optionale Beschreibung des Events..."
              disabled={loading}
            />
          </div>

          {/* Start/End Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start */}
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start *
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>
            </div>

            {/* End */}
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ende *
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Link */}
          <div>
            <label htmlFor="link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link *
            </label>
            <input
              type="url"
              id="link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://..."
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Externer Link zum Event (z.B. Zoom, Teams, YouTube)
            </p>
          </div>

          <EventFeatureFilterEditor
            isPublic={isPublic}
            onIsPublicChange={setIsPublic}
            features={features}
            onFeaturesChange={setFeatures}
            excludeCommunityMembers={excludeCommunityMembers}
            onExcludeCommunityMembersChange={setExcludeCommunityMembers}
            onlyCommunityMembers={onlyCommunityMembers}
            onOnlyCommunityMembersChange={setOnlyCommunityMembers}
            disabled={loading}
            entityLabel="Event"
          />

          <ReminderMinutesEditor rows={reminderRows} onChange={setReminderRows} disabled={loading} />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !subject.trim() || !startDate || !startTime || !endDate || !endTime || !link.trim() || (!isPublic && features.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Erstelle...
              </>
            ) : (
              'Event erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
